import { AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * TabWarnings — 2-column contrasting editorial lists.
 * Left column (Watch-outs): rose top-border.
 * Right column (Strengths): emerald top-border.
 *
 * Testids preserved: tab-warnings, warning-row-*
 */

const CATEGORY_KEYWORDS = {
  Housing:  ["accommod", "housing", "rent", "landlord"],
  Culture:  ["culture", "tradition", "formal", "alienat", "closed", "elit"],
  Academic: ["term", "pace", "workload", "intense", "curriculum"],
  Cost:     ["cost", "expensive", "unaffordable", "fee", "living"],
  Access:   ["access", "outside", "commut", "transport", "college"],
};

function classify(text) {
  const t = (text || "").toLowerCase();
  let severity = "minor";
  if (/rising fast|very high|can feel|hard to|difficult/.test(t)) severity = "moderate";
  if (/unaffordable|extreme|poor|dangerous|serious|abusive/.test(t)) severity = "critical";
  let category = "General";
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some((k) => t.includes(k))) { category = cat; break; }
  }
  return { severity, category };
}

const SEV_BORDER = {
  minor:    "border-amber-400",
  moderate: "border-orange-500",
  critical: "border-rose-600",
};

function WarningRow({ text }) {
  const raw = typeof text === "string" ? { text } : (text || {});
  const cls = raw.severity && raw.category
    ? { severity: raw.severity, category: raw.category }
    : classify(raw.text);
  return (
    <li
      className={`grid grid-cols-[auto_1fr_auto] gap-3 items-start bg-[#F3F1EC]/50 border-l-2 ${SEV_BORDER[cls.severity]} p-4`}
      data-testid={`warning-row-${cls.severity}`}
    >
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-rose-700" />
      <div className="font-sans text-sm leading-relaxed text-[#002147]">
        <div>{raw.text}</div>
        {raw.evidence_url && (
          <a href={raw.evidence_url} target="_blank" rel="noopener noreferrer"
             className="mt-2 inline-block font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline">
            Evidence · {new URL(raw.evidence_url).hostname}
          </a>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="font-mono text-[9px] uppercase border border-[#002147]/20 bg-[#F3F1EC] px-2 py-0.5 rounded-none tracking-wider">
          {cls.severity}
        </span>
        <span className="font-mono text-[9px] uppercase border border-[#002147]/20 bg-[#F3F1EC] px-2 py-0.5 rounded-none tracking-wider">
          {cls.category}
        </span>
      </div>
    </li>
  );
}

function StrengthRow({ text }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-3 items-start bg-[#F3F1EC]/50 border-l-2 border-emerald-600 p-4">
      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-700" />
      <span className="font-sans text-sm leading-relaxed text-[#002147]">{text}</span>
    </li>
  );
}

export default function TabWarnings({ uni }) {
  const warnings = uni.warnings || [];
  const strengths = uni.strengths || [];
  return (
    <section className="space-y-6" data-testid="tab-warnings">
      <div>
        <TabKicker>01 · Editorial Signals</TabKicker>
        <TabSectionTitle>What the numbers can't tell you</TabSectionTitle>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        <div className="border-t-4 border-rose-600 bg-rose-50/30 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-rose-800">
              Watch-outs
            </span>
            <span className="font-mono text-xs text-rose-800/70">
              N · {warnings.length}
            </span>
          </div>
          {warnings.length ? (
            <ul className="space-y-3">
              {warnings.map((w, i) => <WarningRow key={i} text={w} />)}
            </ul>
          ) : (
            <p className="font-sans text-sm text-[#002147]/60">
              No structured watch-outs recorded for this university.
            </p>
          )}
          <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 border-t border-rose-200 pt-3 leading-relaxed">
            <TrendingUp className="inline h-3 w-3 mr-1" />
            Editorial patterns backed by university data, NSS, and student forums.
            Cross-referenced against Reviews as submissions grow.
          </p>
        </div>

        <div className="border-t-4 border-emerald-600 bg-emerald-50/30 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-800">
              Real strengths
            </span>
            <span className="font-mono text-xs text-emerald-800/70">
              N · {strengths.length}
            </span>
          </div>
          {strengths.length ? (
            <ul className="space-y-3">
              {strengths.map((s, i) => <StrengthRow key={i} text={s} />)}
            </ul>
          ) : (
            <p className="font-sans text-sm text-[#002147]/60">
              No standout strengths recorded.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
